import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { ENVIRONMENT } from '@/lib/environment';
import { appendAuthDebugLog } from '@/lib/auth-debug';

type EnsureFreshAuthReason =
  | 'no_session'
  | 'refresh_failed'
  | 'verify_failed'
  | 'network_error'
  | 'unexpected_error';

export interface EnsureFreshAuthResult {
  ok: boolean;
  rotated: boolean;
  reason?: EnsureFreshAuthReason;
  attempts: number;
  source?: string;
}

export interface EnsureFreshAuthOptions {
  force?: boolean;
  source?: string;
}

type EnsureFreshAuthObserver = (result: EnsureFreshAuthResult & { phase: 'start' | 'success' | 'fail' }) => void;

const observers = new Set<EnsureFreshAuthObserver>();

export function subscribeEnsureFreshAuth(observer: EnsureFreshAuthObserver): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

let inFlightPromise: Promise<EnsureFreshAuthResult> | null = null;
let inFlightStartedAt = 0;
let inFlightWatchdog: ReturnType<typeof setTimeout> | null = null;

// CS-108: refresh proactively at send time when the token has <= 5 minutes
// left (was 60s), so a slow refresh happens while the old token still works.
export const EXPIRY_WINDOW_SECONDS = 300;
const MAX_ATTEMPTS = 4;
const BASE_DELAY_MS = 350;
// CS-108: the 0.8s / 1s / 1.5s bounds below were tighter than a refresh on
// slow wifi; one readiness timeout of 5s replaces them.
export const READINESS_TIMEOUT_MS = 5000;
// Joiners and the stale watchdog must outlast a single refresh attempt, or a
// second caller starts a parallel refresh against a rotating refresh token.
const JOIN_TIMEOUT_MS = READINESS_TIMEOUT_MS + 1000;
const INFLIGHT_STALE_MS = 3 * READINESS_TIMEOUT_MS;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Promise timeout helper: returns the original result or the string 'timeout'
const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T | 'timeout'> => {
  return new Promise<T | 'timeout'>((resolve) => {
    const timer = setTimeout(() => {
      try { appendAuthDebugLog({ type: `ensureFreshAuth:${label}:timeout`, ms }); } catch { /* no-op */ }
      resolve('timeout');
    }, ms);
    promise.then((value) => {
      clearTimeout(timer);
      resolve(value);
    }).catch((_err) => {
      clearTimeout(timer);
      // Treat errors as regular rejections to be handled by callers; not timeouts
      resolve('timeout');
    });
  });
};

const notify = (payload: EnsureFreshAuthResult & { phase: 'start' | 'success' | 'fail' }) => {
  appendAuthDebugLog({ type: 'ensureFreshAuth', payload });
  observers.forEach((observer) => {
    try {
      observer(payload);
    } catch (err) {
      logger.debug('ensureFreshAuth: observer threw', { err: err instanceof Error ? err.message : String(err) });
    }
  });
};

/**
 * Perform a direct network verification against /auth/v1/user using the
 * current access token. This bypasses any SDK caching ambiguity and gives a
 * definitive answer from the auth service.
 */
export const verifyUserNetwork = async (): Promise<boolean> => {
  try {
    const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
    // Bound getSession to avoid SDK stall; fallback to localStorage token if needed
    const sessionOrTimeout = await withTimeout(supabase.auth.getSession(), READINESS_TIMEOUT_MS, 'getSession');
    let token: string | undefined;
    if (sessionOrTimeout !== 'timeout') {
      token = sessionOrTimeout?.data?.session?.access_token;
    }
    if (!token && typeof window !== 'undefined' && SUPABASE_URL) {
      try {
        const projectRef = new URL(SUPABASE_URL).hostname.split('.')[0];
        const key = `sb-${projectRef}-auth-token`;
        const raw = window.localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw) as { access_token?: string };
          token = parsed?.access_token;
        }
      } catch {
        // ignore localStorage parse errors
      }
    }
    if (!SUPABASE_URL || !token) return false;
    const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      method: 'GET',
      headers: {
        apikey: (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || '',
        Authorization: `Bearer ${token}`,
      },
    });
    return res.ok;
  } catch (err) {
    logger.debug('ensureFreshAuth: network verify failed', { err: err instanceof Error ? err.message : String(err) });
    return false;
  }
};

const computeSecondsUntilExpiry = async (): Promise<number | undefined> => {
  try {
    // Bound getSession to avoid potential stall
    const result = await withTimeout(supabase.auth.getSession(), READINESS_TIMEOUT_MS, 'getSession:expiry');
    const session = result !== 'timeout' ? result?.data?.session : undefined;
    if (!session?.expires_at) return undefined;
    return Math.floor(session.expires_at - Date.now() / 1000);
  } catch (err) {
    logger.debug('ensureFreshAuth: failed to compute expiry', { err: err instanceof Error ? err.message : String(err) });
    return undefined;
  }
};

const verifyUser = async (): Promise<boolean> => {
  try {
    // Race getUser against a short timeout; fall back to network verify if it stalls
    const quick = await withTimeout(supabase.auth.getUser(), READINESS_TIMEOUT_MS, 'getUser');
    if (quick !== 'timeout') {
      const has = !!quick.data?.user;
      if (has) return true;
    } else {
      logger.debug('ensureFreshAuth: getUser timed out, falling back to network verify');
    }
    // Fallback to direct network verification to avoid false negatives
    return await verifyUserNetwork();
  } catch (err) {
    logger.debug('ensureFreshAuth: getUser failed', { err: err instanceof Error ? err.message : String(err) });
    // Try network verify if SDK call threw
    return await verifyUserNetwork();
  }
};

export async function ensureFreshAuth(options?: EnsureFreshAuthOptions): Promise<EnsureFreshAuthResult> {
  const { force = false, source } = options ?? {};
  if (inFlightPromise) {
    logger.debug('ensureFreshAuth: joining in-flight refresh', { source, sinceMs: Math.max(0, Date.now() - inFlightStartedAt) });
    appendAuthDebugLog({ type: 'ensureFreshAuth:join', sinceMs: Math.max(0, Date.now() - inFlightStartedAt) });
    // Join the in-flight refresh, but do not wait forever
    const joined = await withTimeout(inFlightPromise, JOIN_TIMEOUT_MS, 'join');
    if (joined !== 'timeout') {
      return joined;
    }
    // Join timed out → treat current in-flight as stale for new callers
    appendAuthDebugLog({ type: 'ensureFreshAuth:join-timeout', ageMs: Math.max(0, Date.now() - inFlightStartedAt) });
    logger.warn('ensureFreshAuth: join timeout; starting fresh attempt', { source });
    inFlightPromise = null;
  }

  let attempts = 0;
  let rotated = false;
  const emitStart = () => notify({ ok: false, rotated: false, attempts, reason: undefined, phase: 'start', source });

  const current = (async (): Promise<EnsureFreshAuthResult> => {
    emitStart();
    try {
      while (attempts < MAX_ATTEMPTS) {
        attempts += 1;
        const secondsUntilExpiry = await computeSecondsUntilExpiry();
        const hasUser = await verifyUser();
        const nearExpiry = typeof secondsUntilExpiry === 'number' && secondsUntilExpiry <= EXPIRY_WINDOW_SECONDS;

        if (!force && hasUser && !nearExpiry) {
          const result = { ok: true, rotated, reason: undefined, attempts, source };
          notify({ ...result, phase: 'success' });
          return result;
        }

        logger.info('ensureFreshAuth: refreshing session', { attempt: attempts, nearExpiry, hasUser, force, source });
        // Race the SDK refresh against a short timeout; on timeout try network verify
        const refresh = await withTimeout(supabase.auth.refreshSession(), READINESS_TIMEOUT_MS, 'refreshSession');
        if (refresh === 'timeout') {
          appendAuthDebugLog({ type: 'ensureFreshAuth:refresh-timeout', attempt: attempts });
          const verified = await verifyUserNetwork();
          if (verified) {
            const result: EnsureFreshAuthResult = { ok: true, rotated, reason: undefined, attempts, source };
            notify({ ...result, phase: 'success' });
            return result;
          }
          // treat as transient failure and retry/backoff unless max attempts
          if (attempts >= MAX_ATTEMPTS) {
            const result: EnsureFreshAuthResult = { ok: false, rotated, reason: 'refresh_failed', attempts, source };
            notify({ ...result, phase: 'fail' });
            return result;
          }
          const backoff = BASE_DELAY_MS * Math.pow(2, attempts - 1) + Math.random() * 250;
          await wait(backoff);
          continue;
        }
        if (refresh.error) {
          logger.warn('ensureFreshAuth: refreshSession failed', { attempt: attempts, error: refresh.error.message, status: refresh.error.status });
          appendAuthDebugLog({
            type: 'ensureFreshAuth:refresh-error',
            attempt: attempts,
            message: refresh.error.message,
            status: refresh.error.status,
          });
          const shouldStop = attempts >= MAX_ATTEMPTS || refresh.error?.status === 400 || refresh.error?.status === 401;
          if (shouldStop) {
            const reason: EnsureFreshAuthReason = refresh.error.status === 401 ? 'no_session' : 'refresh_failed';
            const result: EnsureFreshAuthResult = { ok: false, rotated, reason, attempts, source };
            notify({ ...result, phase: 'fail' });
            return result;
          }
          const backoff = BASE_DELAY_MS * Math.pow(2, attempts - 1) + Math.random() * 250;
          await wait(backoff);
          continue;
        }

        rotated = true;
        appendAuthDebugLog({ type: 'ensureFreshAuth:refresh-success', attempt: attempts, rotated: true });

        if (await verifyUser()) {
          const result: EnsureFreshAuthResult = { ok: true, rotated, reason: undefined, attempts, source };
          notify({ ...result, phase: 'success' });
          return result;
        }

        logger.warn('ensureFreshAuth: verification failed after refresh', { attempt: attempts });
        appendAuthDebugLog({ type: 'ensureFreshAuth:verify-failed', attempt: attempts });
        if (attempts >= MAX_ATTEMPTS) {
          const result: EnsureFreshAuthResult = { ok: false, rotated, reason: 'verify_failed', attempts, source };
          notify({ ...result, phase: 'fail' });
          return result;
        }
        const propagationDelay = BASE_DELAY_MS * Math.pow(2, attempts - 1) + Math.random() * 300;
        await wait(propagationDelay);
      }

      const result: EnsureFreshAuthResult = { ok: false, rotated, reason: 'verify_failed', attempts, source };
      notify({ ...result, phase: 'fail' });
      return result;
    } catch (e) {
      logger.error('ensureFreshAuth: unexpected error', e);
      const result: EnsureFreshAuthResult = { ok: false, rotated, reason: 'unexpected_error', attempts, source };
      notify({ ...result, phase: 'fail' });
      return result;
    } finally {
      // Clear in-flight markers
      inFlightPromise = null;
      inFlightStartedAt = 0;
      if (inFlightWatchdog) {
        clearTimeout(inFlightWatchdog);
        inFlightWatchdog = null;
      }
    }
  })();

  // Publish as the new in-flight promise with a stale watchdog
  inFlightPromise = current;
  inFlightStartedAt = Date.now();
  if (inFlightWatchdog) {
    clearTimeout(inFlightWatchdog);
  }
  inFlightWatchdog = setTimeout(() => {
    // Do not force-reject current; just mark as stale so future callers won't join indefinitely
    if (inFlightPromise === current) {
      appendAuthDebugLog({ type: 'ensureFreshAuth:inflight-stale', ageMs: Math.max(0, Date.now() - inFlightStartedAt) });
      logger.warn('ensureFreshAuth: in-flight refresh appears stale; allowing new callers to start fresh');
      inFlightPromise = null;
      inFlightStartedAt = 0;
    }
  }, INFLIGHT_STALE_MS);

  return current;
}

export async function forceRefreshAuth(source?: string): Promise<EnsureFreshAuthResult> {
  return ensureFreshAuth({ force: true, source });
}

export type { EnsureFreshAuthReason };


